class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!window.TWFCartUpsellReady) {
  window.TWFCartUpsellReady = true;

  const formatTWFCartMoney = (cents, currency = 'USD') => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const isTWFLightingCartItem = (item) => {
    const text = [
      item.product_type,
      item.vendor,
      item.handle,
      item.product_title,
      item.title,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return (
      text.includes('gglighting') ||
      text.includes('light') ||
      text.includes('lighting') ||
      text.includes('u-con') ||
      text.includes('accessor') ||
      text.includes('sdr') ||
      text.includes('bumper') ||
      text.includes('tire carrier') ||
      text.includes('roof rack')
    );
  };

  const updateTWFLightingPromo = async () => {
    const promos = document.querySelectorAll('[data-twf-lighting-promo]');
    if (!promos.length) return;

    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    const lightingTotal = (cart.items || []).reduce((total, item) => {
      return isTWFLightingCartItem(item) ? total + Number(item.final_line_price || item.line_price || 0) : total;
    }, 0);
    const currency = cart.currency || window.Shopify?.currency?.active || 'USD';

    promos.forEach((promo) => {
      const threshold = Number(promo.dataset.thresholdCents || 60000);
      promo.dataset.currentCents = String(lightingTotal);
      promo.classList.toggle('is-unlocked', lightingTotal >= threshold);
    });
  };

  document.addEventListener('click', async (event) => {
    const productButton = event.target.closest('[data-twf-upsell-product-add]');
    if (productButton) {
      event.preventDefault();
      const productUrl = productButton.dataset.productUrl;
      if (productUrl) {
        window.location.href = productUrl;
        return;
      }
      const label = productButton.querySelector('[data-twf-upsell-product-label]');
      const variantId = productButton.dataset.variantId;
      if (!variantId || productButton.classList.contains('is-added')) return;

      productButton.disabled = true;
      productButton.classList.add('is-loading');
      if (label) label.textContent = 'Adding';

      try {
        await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        });

        productButton.classList.add('is-added');
        if (label) label.textContent = 'Added';
        await updateTWFLightingPromo();
      } catch (error) {
        productButton.disabled = false;
        if (label) label.textContent = 'Add';
        console.error(error);
      } finally {
        productButton.classList.remove('is-loading');
      }
      return;
    }

    const button = event.target.closest('[data-twf-upsell-request]');
    if (!button) return;

    event.preventDefault();
    const label = button.querySelector('[data-twf-upsell-request-label]');
    const requestName = button.dataset.twfUpsellRequest;
    if (!requestName || button.classList.contains('is-added')) return;

    button.disabled = true;
    button.classList.add('is-loading');
    if (label) label.textContent = 'Adding';

    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      const attributes = cart.attributes || {};
      const attributeName = 'Requested Build Add-Ons';
      const current = (attributes[attributeName] || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const next = current.includes(requestName) ? current : [...current, requestName];

      await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            ...attributes,
            [attributeName]: next.join(', '),
          },
        }),
      });

      button.classList.add('is-added');
      if (label) label.textContent = 'Added';
    } catch (error) {
      button.disabled = false;
      if (label) label.textContent = 'Add';
      console.error(error);
    } finally {
      button.classList.remove('is-loading');
    }
  });
}
